import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Phone, PhoneOff, Mic, MicOff, Loader2 } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useIsMobile } from '@/hooks/use-mobile';
import divusLogoWhite from '@/assets/divus-logo-white.png';

export default function CallModal({ 
  isOpen, 
  onClose, 
  contactName, 
  contactPhone,
  callStatus = 'idle', // idle, connecting, ringing, connected, ended, failed
  errorMessage = null,
  onEndCall,
  onToggleMute,
  isMuted: externalIsMuted = false
}) {
  const isMobile = useIsMobile();
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(externalIsMuted);

  useEffect(() => {
    let interval;
    if (callStatus === 'connected') {
      interval = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [callStatus]);

  useEffect(() => {
    if (!isOpen) {
      setDuration(0);
      setIsMuted(false);
    }
  }, [isOpen]);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusText = () => {
    switch (callStatus) {
      case 'connecting':
        return 'Conectando...';
      case 'ringing':
        return 'Chamando...';
      case 'connected':
        return formatDuration(duration);
      case 'ended':
        return 'Chamada encerrada';
      case 'failed':
        return errorMessage || 'Chamada não concluída';
      default:
        return '';
    }
  };

  useEffect(() => {
    setIsMuted(externalIsMuted);
  }, [externalIsMuted]);

  const handleEndCall = () => {
    if (onEndCall) {
      onEndCall();
    }
    onClose();
  };

  const handleToggleMute = () => {
    if (onToggleMute) {
      const newMutedState = onToggleMute();
      setIsMuted(newMutedState);
    }
  };

  const initials = contactName
    ?.split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '??';

  // Mobile fullscreen version
  if (isMobile && isOpen) {
    return (
      <div className="fixed inset-0 z-[100] bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-between" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
        {/* Logo at top */}
        <div className="pt-6">
          <img src={divusLogoWhite} alt="Divus Legal Group" className="h-8 w-auto opacity-90" />
        </div>
        
        {/* Top section with contact info */}
        <div className="flex-1 flex flex-col items-center justify-center px-6">
          {/* Avatar with animation */}
          <div className="relative">
            {(callStatus === 'ringing' || callStatus === 'connecting') && (
              <>
                <span className="absolute inset-0 rounded-full bg-primary/30 animate-ping scale-110" />
                <span className="absolute inset-0 rounded-full bg-primary/20 animate-pulse scale-105" />
              </>
            )}
            <div className="absolute inset-0 bg-primary/20 rounded-full blur-2xl scale-150" />
            <Avatar className="relative w-32 h-32 border-4 border-white/20 shadow-2xl">
              <AvatarFallback className="text-4xl bg-gradient-to-br from-primary to-primary/80 text-white font-bold">
                {initials}
              </AvatarFallback>
            </Avatar>
          </div>

          {/* Contact info */}
          <h2 className="text-3xl font-bold text-white mt-8">{contactName || 'Contato'}</h2>
          <p className="text-lg text-white/70 mt-2">{contactPhone}</p>

          {/* Status */}
          <div className="mt-8 flex items-center gap-3 px-6 py-4 rounded-full bg-white/10 backdrop-blur-sm">
            {callStatus === 'connecting' && (
              <Loader2 className="w-6 h-6 animate-spin text-white" />
            )}
            {callStatus === 'ringing' && (
              <div className="w-3 h-3 bg-primary rounded-full animate-pulse" />
            )}
            {callStatus === 'connected' && (
              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse" />
            )}
            <p className={`text-2xl font-bold ${
              callStatus === 'connected' ? 'text-green-400' : 
              callStatus === 'failed' ? 'text-red-400' : 'text-white'
            }`}>
              {getStatusText()}
            </p>
          </div>

          {/* Error message */}
          {callStatus === 'failed' && errorMessage && (
            <div className="mt-4 w-full max-w-xs px-4 py-3 bg-red-500/20 border border-red-500/30 rounded-lg">
              <p className="text-sm text-red-300 text-center">⚠️ {errorMessage}</p>
            </div>
          )}

          {/* Status messages */}
          {callStatus === 'connecting' && (
            <div className="mt-4 flex items-center gap-2 px-4 py-2 rounded-full bg-white/5">
              <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
              <p className="text-sm text-white/60">Conectando ao seu headset...</p>
            </div>
          )}
          {callStatus === 'ringing' && (
            <div className="mt-4 flex items-center gap-2 px-4 py-2 rounded-full bg-white/5">
              <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
              <p className="text-sm text-white/60">Aguardando o contato atender</p>
            </div>
          )}
        </div>

        {/* Bottom controls */}
        <div className="w-full px-8 pb-8 pt-8">
          <div className="flex items-center justify-center gap-8">
            {/* Mute button - only when connected */}
            {callStatus === 'connected' && (
              <div className="flex flex-col items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  className={`w-16 h-16 rounded-full transition-all active:scale-95 ${
                    isMuted 
                      ? 'bg-red-500/20 text-red-400 ring-2 ring-red-500/30' 
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                  onClick={handleToggleMute}
                >
                  {isMuted ? <MicOff className="w-7 h-7" /> : <Mic className="w-7 h-7" />}
                </Button>
                <span className="text-white/60 text-xs">{isMuted ? 'Desmutar' : 'Mutar'}</span>
              </div>
            )}

            {/* End call button */}
            <div className="flex flex-col items-center gap-2">
              <Button
                variant={callStatus === 'ended' || callStatus === 'failed' ? 'outline' : 'destructive'}
                size="icon"
                className={`w-20 h-20 rounded-full shadow-2xl transition-all active:scale-95 ${
                  callStatus === 'connected' 
                    ? 'bg-gradient-to-br from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 ring-4 ring-red-500/30' 
                    : callStatus === 'ended' || callStatus === 'failed'
                    ? 'bg-white/10 border-white/20 text-white hover:bg-white/20'
                    : 'bg-gradient-to-br from-red-500 to-red-600'
                }`}
                onClick={handleEndCall}
                disabled={callStatus === 'connecting'}
              >
                <PhoneOff className="w-8 h-8" />
              </Button>
              <span className="text-white/60 text-xs">
                {callStatus === 'ended' || callStatus === 'failed' ? 'Fechar' : 'Encerrar'}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Desktop dialog version
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md border-0 bg-gradient-to-br from-background via-background to-primary/5 shadow-2xl">
        <div className="flex flex-col items-center py-12 space-y-8">
          {/* Avatar with modern pulse animation */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-primary/5 rounded-full blur-2xl scale-150" />
            <Avatar className="relative w-32 h-32 border-4 border-background shadow-xl ring-4 ring-primary/10">
              <AvatarFallback className="text-3xl bg-gradient-to-br from-primary to-primary/80 text-primary-foreground font-semibold">
                {initials}
              </AvatarFallback>
            </Avatar>
            {(callStatus === 'ringing' || callStatus === 'connecting') && (
              <>
                <span className="absolute inset-0 rounded-full bg-primary/30 animate-ping" />
                <span className="absolute inset-2 rounded-full bg-primary/20 animate-pulse" />
                <span className="absolute inset-4 rounded-full bg-primary/10 animate-ping" style={{ animationDelay: '0.5s' }} />
              </>
            )}
          </div>

          {/* Contact name and number */}
          <div className="text-center space-y-2">
            <h3 className="text-2xl font-bold text-foreground tracking-tight">
              {contactName || 'Contato'}
            </h3>
            <p className="text-base text-muted-foreground font-medium">{contactPhone}</p>
          </div>

          {/* Call status */}
          <div className="flex items-center justify-center gap-2 min-h-[48px] px-6 py-3 rounded-full bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/10">
            {callStatus === 'connecting' && (
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
            )}
            <p className={`text-2xl font-bold tabular-nums ${
              callStatus === 'connected' ? 'text-primary' : 
              callStatus === 'failed' ? 'text-destructive' : 'text-muted-foreground'
            }`}>
              {getStatusText()}
            </p>
          </div>

          {/* Error message section */}
          {callStatus === 'failed' && errorMessage && (
            <div className="w-full px-6 py-4 bg-destructive/10 border border-destructive/20 rounded-lg">
              <p className="text-sm text-destructive font-medium text-center">
                ⚠️ {errorMessage}
              </p>
            </div>
          )}

          {/* Call controls */}
          <div className="flex items-center gap-6 pt-4">
            {callStatus === 'connected' && (
              <Button
                variant="outline"
                size="icon"
                className={`w-16 h-16 rounded-full border-2 transition-all duration-300 shadow-lg ${
                  isMuted 
                    ? 'bg-destructive/10 border-destructive/30 hover:bg-destructive/20 text-destructive' 
                    : 'bg-background/80 border-border hover:bg-accent hover:border-primary/30'
                }`}
                onClick={handleToggleMute}
              >
                {isMuted ? (
                  <MicOff className="w-6 h-6" />
                ) : (
                  <Mic className="w-6 h-6" />
                )}
              </Button>
            )}
            
            <Button
              variant={callStatus === 'ended' || callStatus === 'failed' ? 'outline' : 'destructive'}
              size="icon"
              className={`w-20 h-20 rounded-full shadow-2xl transition-all duration-300 ${
                callStatus === 'connected' 
                  ? 'bg-gradient-to-br from-destructive to-destructive/80 hover:from-destructive/90 hover:to-destructive/70 ring-4 ring-destructive/20' 
                  : ''
              }`}
              onClick={handleEndCall}
              disabled={callStatus === 'connecting'}
            >
              <PhoneOff className="w-8 h-8" />
            </Button>
          </div>

          {/* Additional status */}
          {callStatus === 'connecting' && (
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-muted/50">
              <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
               <p className="text-sm text-muted-foreground font-medium">
                Conectando ao seu headset...
              </p>
            </div>
          )}
          {callStatus === 'ringing' && (
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-muted/50">
              <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
               <p className="text-sm text-muted-foreground font-medium">
                Aguardando o contato atender
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
